// Pure helpers of the metadata form and of the code preview (GED-FE-03): what a folder name says
// about its documents, the code the organisation's pattern will give, and the title a file gets
// by default. The code itself is generated by the API; the preview uses the same generator.
import {
  DISCIPLINES,
  DOCUMENT_TYPES,
  PHASE_TYPES,
  generateDocumentCode,
  firstRevisionIndex,
  type Discipline,
  type DocumentSettings,
  type DocumentType,
  type NamingField,
  type PhaseType,
} from '@batiflow/contracts';

export { DISCIPLINES, DOCUMENT_TYPES, PHASE_TYPES };
export type { Discipline, DocumentType, PhaseType };

/** The metadata people give a document (the empty string is "not set" in the form). */
export interface MetadataValues {
  discipline: Discipline | null;
  phaseType: PhaseType | null;
  docType: DocumentType | null;
  zone: string;
  scale: string;
}

export const EMPTY_METADATA: MetadataValues = {
  discipline: null,
  phaseType: null,
  docType: null,
  zone: '',
  scale: '',
};

export const isDiscipline = (value: string): value is Discipline =>
  (DISCIPLINES as readonly string[]).includes(value);
export const isPhaseType = (value: string): value is PhaseType =>
  (PHASE_TYPES as readonly string[]).includes(value);
export const isDocumentType = (value: string): value is DocumentType =>
  (DOCUMENT_TYPES as readonly string[]).includes(value);

// --- What a folder name says.

/** Lower case, without accents or punctuation: "02 - Études" becomes " 02 etudes". */
const plain = (name: string): string =>
  ` ${name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()} `;

/** Whole words (or phrases) of the plain name: " apd " does not match inside "capd". */
const has = (name: string, ...words: string[]): boolean =>
  words.some((word) => name.includes(` ${word} `));

const PHASE_WORDS: readonly (readonly [PhaseType, readonly string[]])[] = [
  ['esq', ['esq', 'esquisse']],
  ['aps', ['aps', 'avant projet sommaire']],
  ['apd', ['apd', 'avant projet detaille']],
  ['pro', ['pro']],
  ['dce', ['dce', 'dossier de consultation']],
  ['act', ['act']],
  ['det', ['det', 'chantier']],
  ['aor_final', ['reception definitive']],
  ['aor_provisional', ['reception', 'reception provisoire']],
];

const DISCIPLINE_WORDS: readonly (readonly [Discipline, readonly string[]])[] = [
  ['architecture', ['architecture']],
  ['structure', ['structure', 'structures', 'genie civil', 'beton arme']],
  ['vrd', ['vrd']],
  ['fluides', ['fluides', 'fluide', 'plomberie', 'cvc']],
  ['electricite', ['electricite', 'courants forts', 'courants faibles']],
  ['topographie', ['topographie', 'topo']],
  ['geotechnique', ['geotechnique', 'sol', 'etude de sol']],
  ['opc', ['opc']],
];

const TYPE_WORDS: readonly (readonly [DocumentType, readonly string[]])[] = [
  ['drawing', ['pieces graphiques', 'plans', 'plan']],
  ['specification', ['pieces ecrites', 'cctp', 'cahier des charges']],
  ['bill_of_quantities', ['bordereaux', 'bordereau', 'bpu', 'dqe']],
  ['minutes', ['comptes rendus', 'compte rendu', 'proces verbaux', 'proces verbal']],
  ['correspondence', ['correspondances', 'correspondance', 'courriers']],
  ['photo', ['photos', 'photo']],
];

/** The first entry of the table (in order) that the nearest folder name saying something matches. */
function pick<T extends string>(
  names: readonly string[],
  table: readonly (readonly [T, readonly string[]])[],
): T | null {
  for (const name of names) {
    const found = table.find(([, words]) => has(name, ...words));
    if (found) return found[0];
  }
  return null;
}

/**
 * What the names of the open folder and its ancestors say about a new document: a phase ("02 ESQ"
 * is the sketch phase, "03 - Dossier de consultation" is DCE), a discipline ("Structure") and a
 * type of document ("Photos"). `names` run from the open folder up to the root: the nearest folder
 * that says something wins. Only a suggestion: the person changes it in the form.
 */
export function suggestFromFolders(
  names: readonly string[],
): Pick<MetadataValues, 'discipline' | 'phaseType' | 'docType'> {
  const plainNames = names.map(plain);
  return {
    phaseType: pick(plainNames, PHASE_WORDS),
    discipline: pick(plainNames, DISCIPLINE_WORDS),
    docType: pick(plainNames, TYPE_WORDS),
  };
}

// --- Titles.

/** The default title of a file: its name without the extension ("Plan RDC.pdf" is "Plan RDC"). */
export function titleFromFileName(filename: string): string {
  const dot = filename.lastIndexOf('.');
  const stem = dot > 0 ? filename.slice(0, dot) : filename;
  return stem.trim().slice(0, 300) || filename.trim().slice(0, 300);
}

// --- Code preview.

/** What `{NUM}` shows in a preview: BatiFlow gives the number when the file is added. */
export const NUMBER_PLACEHOLDER = '###';

export type CodePreview =
  { ok: true; code: string } | { ok: false; missing: readonly NamingField[] };

/**
 * The code the pattern will give, with `###` for the sequence number (given by BatiFlow on the
 * server) and the first index of the style. `missing` lists the metadata still needed.
 */
export function previewCode(
  settings: DocumentSettings,
  projectCode: string | null,
  values: Pick<MetadataValues, 'discipline' | 'phaseType' | 'docType' | 'zone'>,
): CodePreview {
  const common = {
    projectCode,
    phaseType: values.phaseType,
    discipline: values.discipline,
    docType: values.docType,
    zone: values.zone.trim() === '' ? null : values.zone.trim(),
    number: 0,
    index: firstRevisionIndex(settings.indexStyle),
  };
  const whole = generateDocumentCode(settings.namingPattern, common);
  if (!whole.ok) return { ok: false, missing: whole.missing };
  // Each piece around {NUM} is generated alone, so a project code made of digits is never
  // mistaken for the number.
  const pieces = settings.namingPattern.split('{NUM}').map((piece) => {
    const generated = generateDocumentCode(piece, common);
    return generated.ok ? generated.code : '';
  });
  return { ok: true, code: pieces.join(NUMBER_PLACEHOLDER) };
}
